import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field, change } from 'redux-form';
import { t } from 'i18next';
import AddressComplete from '../../ReactComponent/AddressComplete';
import Row from '../../ReactComponent/Row';
import Column from '../../ReactComponent/Column';
import {
  isRequired, isPhone, isEmail, maxChar50,
  maxChar40,
  maxChar5,
} from '../../../utils/validator';
import { phoneNumber, digitOnly } from '../../../utils/mask';
import {
  PageTitle,
  TextProperty,
  ContactColumn,
  FieldLabel,
  RadioFieldContainer,
  RadioField,
} from '../css';
import { P } from '../../../theme/typography';
import { AddressTypes } from '../../../utils/enums';
import Dropdown from '../../../lib/ui/form_elements/Dropdown';
import TextInput from '../../../lib/ui/form_elements/TextInput';
import RadioButton from '../../../lib/ui/form_elements/RadioButton';
import Tooltip from '../../../lib/ui/form_elements/Tooltip';
import { normalizeBoolean } from '../../../utils/form';

const ContactForm = ({
  formData = {}, formName, companiesList, parentFields, dispatch,
}) => {
  const { rowVersion, nickname } = formData;
  const update = !!rowVersion;
  const [radioOptions, setRadioOption] = useState(null);

  const addressTypeLabels = {
    [AddressTypes.SHIPPER]: t('shipper'),
    [AddressTypes.CONSIGNEE]: t('consignee'),
    [AddressTypes.SHARED]: t('shared'),
    [AddressTypes.OTHER]: t('other'),
  };

  useEffect(() => {
    if (!update) {
      const radioOptions = Object.values(AddressTypes).map((item) => ({
        label: addressTypeLabels[item] || item,
        value: item,
        defaultChecked: false,
      }));
      radioOptions[0].defaultChecked = true;
      dispatch(change(formName, 'contactType', radioOptions[0].value));
      setRadioOption(radioOptions);
    }
  }, []);

  return (
    <>
      <PageTitle className="justified">
        { parentFields.pageHeading && parentFields.pageHeading.value }
      </PageTitle>
      <Row>
        <Column $tablet={6} $mobile={12}>
          <ContactColumn>
            {
                !update
                && (
                <Row>
                  <Column>
                    <Field
                      name="companyId"
                      label={t('SelectCompany')}
                      component={Dropdown}
                      options={companiesList.map((item) => ({ value: item.companyName, id: item.companyId }))}
                      valueField="id"
                      valueFormatter={(option) => option && option.value}
                      validate={[isRequired]}
                    />
                  </Column>
                </Row>
                )
              }
            {
                update
                  ? (
                    <TextProperty>
                      <P className="title">{t('Nickname')}</P>
                      <P className="text">{nickname.name}</P>
                    </TextProperty>
                  )
                  : (
                    <Row>
                      <Column className="field-tooltip">
                        <Field
                          name="nickname"
                          component={TextInput}
                          label={t('Nickname')}
                          validate={[isRequired, maxChar40]}
                          maxLength={40}
                          required
                          tooltip={
                            <Tooltip message={t('TipNickname')} />
                      }
                        />
                      </Column>
                    </Row>
                  )
              }
            <Row>
              <Column>
                <AddressComplete
                  fieldName="address"
                  formName={formName}
                  required
                  tooltipRequired
                />
              </Column>
            </Row>
            <Row>
              <Column>
                <Field
                  name="addressAdditional"
                  type="text"
                  component={TextInput}
                  label={t('AddressLine2')}
                  validate={[maxChar40]}
                  maxLength={40}
                  optional
                />
              </Column>
            </Row>
            <Row className="justify-content-md-center">
              <Column $mobile={8}>
                <Field
                  name="phoneNumber"
                  type="text"
                  component={TextInput}
                  label={t('PhoneNumber')}
                  validate={[isPhone, maxChar50, isRequired]}
                  normalize={phoneNumber}
                  required
                />
              </Column>
              <Column $mobile={4}>
                <Field
                  name="phoneNumberExt"
                  type="text"
                  component={TextInput}
                  label={t('Extension')}
                  validate={[maxChar5]}
                  maxLength={5}
                  normalize={digitOnly}
                />
              </Column>
            </Row>
            <Row>
              <Column $tablet={8}>
                <Field
                  name="mobileNumber"
                  type="text"
                  component={TextInput}
                  label={t('MobileSMS')}
                  validate={[isPhone, maxChar50]}
                  normalize={phoneNumber}
                  optional
                />
              </Column>
            </Row>
          </ContactColumn>
        </Column>
        <Column $tablet={6} $mobile={12}>
          <ContactColumn>
            <Row>
              <Column>
                <Field
                  name="contactName"
                  type="text"
                  required
                  component={TextInput}
                  label={t('FullName')}
                  validate={[isRequired, maxChar40]}
                  maxLength={40}
                />
              </Column>
            </Row>
            <Row>
              <Column className="field-tooltip">
                <Field
                  name="companyName"
                  type="text"
                  required
                  component={TextInput}
                  label={t('ContactCompanyName')}
                  validate={[isRequired, maxChar40]}
                  maxLength={40}
                  tooltip={
                    <Tooltip message={t('TipCompanyName')} />
                    }
                />
              </Column>
            </Row>
            <Row>
              <Column>
                <Field
                  name="email"
                  type="email"
                  component={TextInput}
                  label={t('Email')}
                  validate={[isEmail, maxChar50, isRequired]}
                  required
                />
              </Column>
            </Row>
            {
                !update && radioOptions
                && (
                <Row>
                  <Column>
                    <RadioFieldContainer>
                      <FieldLabel>
                        {t('lblContactType')}
                        <Tooltip message={t('TipContactType')} />
                      </FieldLabel>
                      <RadioField>
                        <Field
                          name="contactType"
                          component={RadioButton}
                          options={radioOptions}
                          normalize={normalizeBoolean}
                          validate={[isRequired]}
                          type="checkboxes"
                          required
                        />
                      </RadioField>
                    </RadioFieldContainer>
                  </Column>
                </Row>
                )
                }
          </ContactColumn>
        </Column>

      </Row>
    </>
  );
};

ContactForm.propTypes = {
  formData: PropTypes.shape({
    rowVersion: PropTypes.string,
    nickname: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
  formName: PropTypes.string,
  companiesList: PropTypes.arrayOf(PropTypes.shape({
    companyName: PropTypes.string,
  })),
  parentFields: PropTypes.shape({
    pageHeading: PropTypes.shape({
      value: PropTypes.string,
    }),
  }),
  dispatch: PropTypes.func,
};

export default ContactForm;
